import type { UntypedFormGroup } from '@angular/forms';
import { NonNullableFormBuilder } from '@angular/forms';
import type { Container, Widget } from '@common/types/content.type';
import type { WidgetErrors } from '@common/types/widget-errors.type';
import type { Classes, JssStyle, StyleSheet } from 'jss';

import { ChangeDetectorRef, Directive, inject, Injector, Input, type OnDestroy, type OnInit } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import type { Observable } from 'rxjs';
import { Subject } from 'rxjs';
import { filter, map, takeUntil, tap } from 'rxjs/operators';

import { CSS_RESTRICTIONS } from '@common/constants';
import { BaseStylesService } from '@common/services/base-styles.service';
import { SpEditorService } from '@components/sp-editor/service/sp-editor.service';

@Directive()
export abstract class SpBaseControlComponent<T, W extends Widget = Widget, C extends Container<W> = Container<W>>
    implements OnInit, OnDestroy
{
    @Input() public container$: Observable<C>;
    @Input() public widget$: Observable<W>;
    @Input() public hasAccess: boolean;

    protected readonly fb: NonNullableFormBuilder = inject(NonNullableFormBuilder);
    protected readonly changeDetectorRef: ChangeDetectorRef = inject(ChangeDetectorRef);
    protected readonly translateService: TranslateService = inject(TranslateService);
    protected readonly stylesService: BaseStylesService = inject(BaseStylesService);

    private readonly spEditorService: SpEditorService = inject(SpEditorService);

    protected readonly unsubscribe$ = new Subject<void>();

    public readonly CSS_RESTRICTIONS = CSS_RESTRICTIONS;

    public container: C;
    public widget: W;
    public controlForm: UntypedFormGroup;
    public classes: Classes;

    private sheet: StyleSheet;

    protected getStyles?: (widget: W, config: { isConstructor: boolean }) => JssStyle;
    protected withContainerIdCallback?: (containerId: string) => any;

    constructor() {
        this.initForm();
    }

    protected abstract initForm(): void;
    protected abstract updateWidgetData(data: Partial<T>): void;
    protected abstract updateWidgetStyles(styles: JssStyle, mobileStyles: JssStyle): void;
    protected abstract updateWidgetErrors(errors: WidgetErrors): void;

    protected beforeFormSubscription(): void {}
    protected initSubscriptions(): void {}

    protected beforeDestroy(): void {}

    public ngOnInit(): void {
        this.beforeFormSubscription();

        this.container$
            .pipe(
                takeUntil(this.unsubscribe$),
                filter((container: C) => Boolean(container)),
                map((container: C) => {
                    const containerId = (container as any).parentId ? (container as any).parentId : container.id;
                    if (this.withContainerIdCallback) {
                        this.withContainerIdCallback(containerId);
                    }
                    return container;
                }),
                tap((container: C) => (this.container = container))
            )
            .subscribe();

        this.widget$
            .pipe(
                takeUntil(this.unsubscribe$),
                filter((widget: W) => Boolean(widget))
            )
            .subscribe((widget: W) => {
                this.widget = widget;
                this.sheet = this.stylesService.getStyles()[this.widget.id];
                this.classes = this.sheet?.classes;
                this.setFormData();
                this.changeDetectorRef.markForCheck();
            });

        this.initFormSubscription();
        this.initSubscriptions();
    }

    public validate<D extends W>(widget: D): WidgetErrors {
        if (!this.controlForm) {
            return null;
        }

        this.widget = widget;
        this.setFormData();

        return this.getWidgetValidationErrors();
    }

    protected getWidgetValidationErrors(): WidgetErrors {
        return null;
    }

    protected setFormData(): void {
        this.controlForm.patchValue(this.widgetData, { emitEvent: false });
    }

    protected initFormSubscription(): void {
        this.controlForm.valueChanges
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe((data) => this.updateWidgetData(data));
    }

    public get widgetData(): T {
        return this.widget.data as T;
    }

    public get widgetStyles(): JssStyle {
        return this.widget.styles;
    }

    public get widgetMobileStyles(): JssStyle {
        return this.widget.mobileStyles;
    }

    public blurEditor() {
        this.spEditorService.simulateBlur();
    }

    public ngOnDestroy(): void {
        this.beforeDestroy();
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }
}
